const DEFAULT_POLL_INTERVAL_MS = 150;
const DEFAULT_POLL_MAX_ATTEMPTS = 40;

interface PollForElementOptions {
  intervalMs?: number;
  maxAttempts?: number;
}

/**
 * Poll `resolve` on an interval until it returns a non-null value, then hand that
 * value to `onFound`. Some targets (an AG Grid row, a menu mid-open) render a few
 * frames after the caller runs, so retry briefly before giving up. Returns a
 * canceler that stops any pending retry; call it on cleanup.
 */
export function pollForElement<T>(
  resolve: () => T | null,
  onFound: (value: T) => void,
  {
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_POLL_MAX_ATTEMPTS,
  }: PollForElementOptions = {},
): () => void {
  let attempts = 0;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let cancelled = false;

  const poll = () => {
    if (cancelled) {
      return;
    }
    const found = resolve();
    if (found !== null) {
      onFound(found);
      return;
    }
    attempts += 1;
    if (attempts < maxAttempts) {
      timeoutId = setTimeout(poll, intervalMs);
    }
  };

  poll();

  return () => {
    cancelled = true;
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  };
}
